import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, History } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { errText, when } from "./shared";

export const MAP_BASE = "/admin/rewards/fulfilment/brand-mappings";

function StoreOption({ store, busy, onPick }) {
  const [sf, setSf] = useState(store.storefronts[0]?.id || "");
  return (
    <li className="py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`gc-store-option-${store.store_id}`}>
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900">{store.name || "Merchant"}</div>
        <div className="text-[11px] text-slate-500">
          {store.website_host || "no website"} · {store.live_offer_count} live offer{store.live_offer_count === 1 ? "" : "s"}
          {!store.visible_to_shoppers && <span className="ml-1 text-rose-700 font-semibold">· hidden from shoppers</span>}
        </div>
        {store.storefronts.length > 1 && (
          <select value={sf} onChange={(e) => setSf(e.target.value)} className="mt-1 text-xs border border-slate-200 rounded px-2 py-1"
                  data-testid={`gc-store-storefront-${store.store_id}`}>
            {store.storefronts.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.status})</option>)}
          </select>
        )}
      </div>
      <button disabled={busy} onClick={() => onPick(store.store_id, sf || null)} data-testid={`gc-store-pick-${store.store_id}`}
              className="pf-btn-primary px-3 py-1 rounded-full text-xs font-semibold disabled:opacity-40">Match &amp; verify</button>
    </li>
  );
}

export function StorePickerDialog({ row, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (row) setQ(row.suggestion?.store_name || row.merchant?.name || ""); }, [row]);
  useEffect(() => {
    if (!row) return undefined;
    setItems(null);
    const t = setTimeout(async () => {
      try { setItems((await api.get(`${MAP_BASE}/store-options`, { params: { q: q || undefined } })).data.items || []); }
      catch { setItems([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, row]);

  const pick = async (storeId, storefrontId) => {
    setBusy(true);
    try {
      await api.put(`${MAP_BASE}/${row.product_id}`, { store_id: storeId, storefront_id: storefrontId });
      toast.success(`${row.brand} matched and verified.`);
      onSaved();
    } catch (e) { toast.error(errText(e, "Could not save the match")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="gc-store-picker">
        <DialogHeader>
          <DialogTitle>Match {row?.brand} to a Cyannect merchant</DialogTitle>
          <DialogDescription>Shoppers holding this gift card will see this merchant's live offers in My Gift Cards.</DialogDescription>
        </DialogHeader>
        <label className="relative block">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search merchants or storefronts"
                 className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="gc-store-search" />
        </label>
        <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100" data-testid="gc-store-options">
          {items === null && <li className="py-6 text-center text-sm text-slate-500" data-testid="gc-store-loading">Loading merchants…</li>}
          {items?.length === 0 && <li className="py-6 text-center text-sm text-slate-500" data-testid="gc-store-empty">No merchants found.</li>}
          {(items || []).map((s) => <StoreOption key={s.store_id} store={s} busy={busy} onPick={pick} />)}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

export function RemoveConfirm({ row, onCancel, onConfirm }) {
  return (
    <AlertDialog open={!!row} onOpenChange={(v) => !v && onCancel()}>
      <AlertDialogContent data-testid="gc-remove-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>{row?.merchant ? `Remove the ${row?.brand} match?` : `Mark ${row?.brand} as no match?`}</AlertDialogTitle>
          <AlertDialogDescription>
            {row?.merchant ? "Shoppers holding this gift card will stop seeing this merchant's offers in My Gift Cards. " : ""}
            The brand will be set to No match. This is logged in the history.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="gc-remove-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-rose-700 hover:bg-rose-800" data-testid="gc-remove-confirm-btn">
            {row?.merchant ? "Remove match" : "Set no match"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const ACTION_LABEL = { carried_over: "Carried over (unverified)", verified: "Verified", changed: "Changed", removed: "Removed", marked_no_match: "Marked no match" };

export function HistoryDialog({ row, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!row) return;
    setItems(null);
    api.get(`${MAP_BASE}/${row.product_id}/history`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [row]);
  const name = (snap) => (snap?.store_id ? snap.store_name || snap.store_id.slice(0, 8) : "none");
  return (
    <Dialog open={!!row} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="gc-mapping-history">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="w-4 h-4" /> {row?.brand} match history</DialogTitle>
          <DialogDescription>Every carry-over, verification, change and removal.</DialogDescription>
        </DialogHeader>
        <ul className="max-h-80 overflow-y-auto divide-y divide-slate-100 text-xs">
          {items?.length === 0 && <li className="py-6 text-center text-slate-500" data-testid="gc-history-empty">No changes yet.</li>}
          {(items || []).map((h, i) => (
            <li key={i} className="py-2" data-testid={`gc-history-item-${i}`}>
              <div className="font-semibold text-slate-900">{ACTION_LABEL[h.action] || h.action}</div>
              <div className="text-slate-600">{name(h.meta?.previous)} → {name(h.meta?.new)} {h.meta?.new?.match_status ? `(${h.meta.new.match_status})` : ""}</div>
              <div className="text-slate-500">{h.actor_email || h.actor_role} · {when(h.created_at)}</div>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
